import React, { useContext, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { spacing } from '../../../../App/ChakraTheme';

import {
  DownloadIcon,
  FolderTreeIcon,
  FullscreenIcon,
  LongArrowLeftIcon,
  TimesSquareIcon,
} from '../../../../components/icons';
import { MediaTypeIcon } from '../../../../scripts/getMediaTypeIcon';
import { GuidesPageWrapper } from './Wrapper';
import { TypeUrl } from '../../hooks/api/types';
import { Page } from '../../../../types';
import { useGetProgram } from '../../hooks/useGetProgram';
import { useGetGuide } from '../../hooks/useGetGuide';
import {
  BackButton,
  LeftColumn,
  LessonName,
  ProgramTitle,
  RightColumn,
  StyledRule,
  ToggleSideBar,
} from './components';
import Loader from '../../../../components/Loader';
import { AuthContext } from '../../../../context/AuthProvider';
import CmsGuide from './CmsGuide';
import ClassPage from '../Class/index';
import { useTranslation } from 'react-i18next';

const timestamp = new Date().getTime();

type Params = { id: string; programId: string };

const GuidesPage: Page = () => {
  const { id, programId } = useParams<Params>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const user = useContext(AuthContext);
  const location = useLocation();
  const { t } = useTranslation();
  const [guide, { isLoading: isGuideLoading }] = useGetGuide(id || '');

  const [program, { isLoading: isProgramLoading }] = useGetProgram(
    programId as any,
    !!programId,
  );

  const [sideBarOpened, setSideBarOpened] = useState(true);

  return (
    <GuidesPageWrapper>
      <ProgramTitle>
        <BackButton
          className="back"
          as={Link}
          to={ClassPage.getRoute(
            program?.pk,
            'preparation-materials',
            location.search,
          )}
          leftIcon={<LongArrowLeftIcon boxSize="16px" />}
        >
          <Text as="div" textStyle="bodyMedium">
            <span className="hide-phone">{t('guidesPage.backToClass')}</span>
          </Text>
        </BackButton>
        {isProgramLoading || !program?.pk ? (
          <Loader />
        ) : (
          <Text
            as="h1"
            textStyle="h4"
            style={{ padding: `0 ${spacing.BASE_PLUS}` }}
            title={program.name || 'Unit'}
            data-lines="4"
          >
            {program.name || 'Class'}
          </Text>
        )}
      </ProgramTitle>
      <VStack w="full">
        <HStack
          w="full"
          flexGrow={1}
          flexWrap="nowrap"
          overflow="hidden"
          mt={5}
          position="relative"
          align="start"
        >
          <ToggleSideBar
            opened={sideBarOpened}
            onClick={() => setSideBarOpened((a) => !a)}
            title={
              sideBarOpened
                ? t('guidesPage.closeSideMenu')
                : t('guidesPage.openSideMenu')
            }
          >
            <IconButton
              zIndex="50"
              position="absolute"
              // mt={-6}
              // top="0"
              // mt="4"
              // left={sideBarOpened ? '286px' : '32px'}
              title={
                sideBarOpened
                  ? t('guidesPage.closeSideMenu')
                  : t('guidesPage.openSideMenu')
              }
              aria-label={
                sideBarOpened
                  ? t('guidesPage.closeSideMenu')
                  : t('guidesPage.openSideMenu')
              }
              fontSize="20px"
              onClick={() => {
                setSideBarOpened(!sideBarOpened);
              }}
              transition="all 0.5s ease"
              icon={sideBarOpened ? <TimesSquareIcon /> : <FolderTreeIcon />}
            />
          </ToggleSideBar>
          <LeftColumn opened={sideBarOpened}>
            <VStack
              w="full"
              className={'content-title-container'}
              justify="start"
              align="start"
            >
              {isProgramLoading && <Loader />}

              {program?.guides?.map((g) => {
                const activeGuide = guide?.pk === g.pk;
                const disabled = user?.is_demo && (!g.isDemo && !g.is_demo);
                return (
                  <Link
                    key={g.pk}
                    style={
                      disabled
                        ? {
                            cursor: 'not-allowed',
                            opacity: 0.5,
                            textDecoration: 'none',
                            pointerEvents: 'none',
                          }
                        : {}
                    }
                    to={GuidesPage.getRoute(programId, g.pk, location.search)}
                  >
                    <LessonName active={activeGuide}>{g.name}</LessonName>
                  </Link>
                );
              })}
            </VStack>
          </LeftColumn>
          <StyledRule className="vertical" sideBarOpened={sideBarOpened} />
          <RightColumn>
            <div
              style={{
                minWidth: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {isGuideLoading || !guide?.pk ? (
                <Loader />
              ) : (
                <>
                  <div
                    style={{
                      width: '100%',
                      flexWrap: 'wrap',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flex: '0 0 auto',
                    }}
                  >
                    <HStack
                      w="fit-content"
                      maxW="full"
                      className={'v-center content-title'}
                    >
                      <MediaTypeIcon
                        boxSize="24px"
                        mediaType={guide.media_type}
                      />
                      <Text as="h4" textStyle="h4">
                        {guide.name}
                      </Text>
                    </HStack>
                    <HStack
                      w="fit-content"
                      maxW="full"
                      className={'v-center content-controls'}
                    >
                      {guide.media_type === 'file' ? (
                        <a
                          href={`/program/${programId}/guide-download/${guide.pk}`}
                          title={t('guidesPage.downloadMaterial')}
                        >
                          <Button
                            colorScheme="blue"
                            leftIcon={<DownloadIcon boxSize="24px" />}
                          >
                            <span className="hide-tablet">
                              {t('toolbar.download')}
                            </span>
                          </Button>
                        </a>
                      ) : null}

                      {guide.media_type !== 'file' ? (
                        <Button
                          colorScheme="blue"
                          title={t('guidesPage.openInFullScreen')}
                          onClick={() => {
                            (
                              iframeRef.current as any
                            )?.webkitRequestFullScreen();
                          }}
                          leftIcon={<FullscreenIcon boxSize="24px" />}
                        >
                          <span className={'hide-tablet'}>
                            {t('guidesPage.fullScreen')}
                          </span>
                        </Button>
                      ) : null}

                      {/*<Button title={'Open My Notes'}>*/}
                      {/*  <Icon size={icons.sizes.MEDIUM} icon={Note}/>*/}
                      {/*  <span className={'hide-tablet'}>My Notes</span>*/}
                      {/*</Button>*/}
                    </HStack>
                  </div>
                  {guide.media_type === 'pdf' ? (
                    <iframe
                      title="PDF guide"
                      ref={iframeRef}
                      src={`/static/ui/pdfjs/web/viewer.html?file=${guide.pdf_url}`}
                      frameBorder="0"
                      allowFullScreen
                    />
                  ) : guide.media_type === 'file' ? (
                    <div style={{ textAlign: 'left', paddingLeft: '10px' }}>
                      <p>
                        {t('guidesPage.toAccess')}
                        {guide.name} {t('guidesPage.clickTheDownloadBtn')}
                      </p>
                      <ul>
                        <li>{guide?.file_url?.substr('/media/'.length)}</li>
                      </ul>
                      {/*{guide.is_downloadable ?*/}
                      {/*    <a href={`/download/${guide.pk}`} title={'Download Material'}>*/}
                      {/*        <span className={'hide-tablet'}>Download</span>*/}
                      {/*    </a> : null}*/}
                    </div>
                  ) : guide.media_type === 'cms' ? (
                    <CmsGuide ref={iframeRef} guide={guide} />
                  ) : (
                    <iframe
                      title={t('guidesPage.otherMediaViaUrl')}
                      ref={iframeRef}
                      src={guide?.[`${guide.media_type}_url` as TypeUrl] || ''}
                      frameBorder="0"
                      allowFullScreen
                    />
                  )}
                </>
              )}
            </div>
          </RightColumn>
        </HStack>
      </VStack>
    </GuidesPageWrapper>
  );
};

GuidesPage.getRoute = (programId = ':programId', id = ':id', query = '') => {
  return `/program/${programId}/guides/${id}/${query}`;
};

export default GuidesPage;
